"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence, motion } from "framer-motion";
import { Clipboard, Loader2, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { SEARCH_SOURCES, SUPPORTED_SEARCH_SOURCES } from "@/schemas/journey";
import { LoadingAnimation } from "../../steps/loading-animation";

const listingFormSchema = z.object({
  url: z
    .string()
    .url("Please enter a valid URL")
    .refine(
      (url) =>
        Object.keys(SEARCH_SOURCES).some((domain) => url.includes(domain)),
      {
        message: "Please enter a URL from a supported listing site",
      },
    ),
});

type ListingFormValues = z.infer<typeof listingFormSchema>;

const LOADING_MESSAGES = [
  { message: "Connecting to listing site...", duration: 2000 },
  { message: "Gathering property details...", duration: 3000 },
  { message: "Processing images...", duration: 3000 },
  { message: "Almost there...", duration: 2000 },
];

interface PropertyInputFormProps {
  onSubmit: (data: ListingFormValues) => Promise<void>;
  onManualAdd?: () => void;
  showManualOption?: boolean;
  defaultPropertyUrl?: string;
}

export function PropertyInputForm({
  onSubmit,
  onManualAdd,
  showManualOption = false,
  defaultPropertyUrl,
}: PropertyInputFormProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [validDomain, setValidDomain] = useState<string | null>(null);
  const { toast } = useToast();

  const form = useForm<ListingFormValues>({
    resolver: zodResolver(listingFormSchema),
    defaultValues: {
      url: defaultPropertyUrl || "",
    },
  });

  useEffect(() => {
    if (!isLoading) {
      setLoadingProgress(0);
      setLoadingMessage("");
      return;
    }

    const totalDuration = LOADING_MESSAGES.reduce(
      (acc, msg) => acc + msg.duration,
      0,
    );
    let elapsed = 0;

    const progressInterval = setInterval(() => {
      setLoadingProgress((prev) => Math.min(prev + 0.5, 95));
    }, 150);

    for (const msg of LOADING_MESSAGES) {
      setTimeout(() => {
        setLoadingMessage(msg.message);
      }, elapsed);
      elapsed += msg.duration;
    }

    return () => {
      clearInterval(progressInterval);
    };
  }, [isLoading]);

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    try {
      const domain = new URL(url).hostname.replace("www.", "");
      const matchedDomain = Object.keys(SEARCH_SOURCES).find((source) =>
        domain.includes(source),
      );
      setValidDomain(matchedDomain || null);
      form.trigger("url");
    } catch {
      setValidDomain(null);
    }
    form.setValue("url", url);
  };

  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      form.setValue("url", text);
      const event = {
        target: { value: text },
      } as React.ChangeEvent<HTMLInputElement>;
      handleUrlChange(event);
    } catch (error) {
      console.error("Failed to read clipboard:", error);
    }
  };

  const handleSubmit = async (data: ListingFormValues) => {
    // Just clean up URL param on submit
    const url = new URL(window.location.href);
    url.searchParams.delete("property_url");
    window.history.replaceState({}, "", url.toString());

    setIsLoading(true);
    try {
      await onSubmit(data);
      form.reset();
      setValidDomain(null);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add property",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="relative">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="relative">
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <div className="relative">
                    <FormControl>
                      <div className="relative flex items-center">
                        {validDomain && (
                          <div className="absolute left-3 bg-white rounded-lg p-1.5">
                            <img
                              src={`https://logo.clearbit.com/${validDomain}`}
                              alt={validDomain}
                              className="w-5 h-5 object-contain"
                            />
                          </div>
                        )}
                        <Input
                          placeholder="https://www.realtor.com/realestateandhomes-detail/..."
                          className={cn(
                            "pr-[140px] h-10 text-base truncate",
                            validDomain ? "pl-11" : "pl-4",
                          )}
                          disabled={isLoading}
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            handleUrlChange(e);
                          }}
                        />
                        <div className="absolute right-2 flex gap-2 h-10 items-center">
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7"
                            onClick={handlePaste}
                          >
                            <Clipboard className="h-4 w-4" />
                          </Button>
                          <Button
                            type="submit"
                            disabled={isLoading}
                            className={cn(
                              "bg-canary hover:bg-canary/90 text-black h-7",
                              defaultPropertyUrl && "animate-scale",
                            )}
                          >
                            {isLoading ? (
                              <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                            ) : (
                              <Plus className="mr-1.5 h-4 w-4" />
                            )}
                            Add
                          </Button>
                        </div>
                      </div>
                    </FormControl>
                    <FormMessage className="mt-2 ml-1" />
                  </div>
                </FormItem>
              )}
            />
          </form>
        </Form>
      </div>

      <div className="flex items-center justify-center gap-2 mt-2">
        {SUPPORTED_SEARCH_SOURCES.map((domain) => (
          <div
            key={domain}
            className="bg-white rounded-lg p-1.5 shadow-sm border border-gray-100"
          >
            <img
              src={`https://logo.clearbit.com/${domain}`}
              alt={domain}
              className="w-5 h-5 object-contain"
              onError={(e) => {
                e.currentTarget.src = "data:image/svg+xml,...";
                e.currentTarget.className = "w-4 h-4 text-gray-400";
              }}
            />
          </div>
        ))}
      </div>

      {showManualOption && (
        <div className="text-center">
          <Button variant="link" onClick={onManualAdd}>
            Or enter details manually
          </Button>
        </div>
      )}

      {/* Loading State */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="rounded-lg bg-white p-6 shadow-lg"
          >
            <LoadingAnimation progress={loadingProgress} />
            <p className="text-center text-gray-600 mt-4">
              Loading property details...
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
